import { Component, useEffect } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { triggerSignInRedirect } from "../api/client.js";

interface ErrorBoundaryProps {
  /** Optional custom fallback UI. Receives the error and a reset function. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** Label for logging — helps identify which boundary caught the error. */
  name?: string;
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Reusable React error boundary.
 *
 * Catches errors thrown during rendering in child components and displays
 * a fallback UI instead of crashing the entire app. Must be a class
 * component because React does not support error boundaries as hooks.
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const label = this.props.name ?? "ErrorBoundary";
    console.error(`[${label}] caught error:`, error, info.componentStack);
  }

  private reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (error) {
      if (isAuthError(error)) {
        window.dispatchEvent(new CustomEvent("pwa-unauthenticated"));
        return <AuthErrorFallback />;
      }
      if (this.props.fallback) {
        return this.props.fallback(error, this.reset);
      }
      return <DefaultFallback error={error} onReset={this.reset} />;
    }
    return this.props.children;
  }
}

// ─── Auth error detection ─────────────────────────────────────

function isAuthError(error: Error): boolean {
  const msg = error.message.toLowerCase();
  return msg.includes("unauthenticated") || msg.includes("401");
}

function AuthErrorFallback() {
  // This used to only CLAIM to redirect — the redirect never actually
  // fired, which is exactly how the operator got trapped here twice
  // (Jul-8). triggerSignInRedirect() does a real top-level navigation,
  // debounced by client.ts's module-level latch.
  useEffect(() => {
    triggerSignInRedirect();
  }, []);

  return (
    <div
      style={{
        padding: "40px 20px",
        textAlign: "center",
        color: "var(--text-muted)",
        fontSize: "14px",
      }}
    >
      <p style={{ margin: "0 0 12px" }}>Your session has expired.</p>
      <p style={{ margin: 0, fontSize: "12px", color: "var(--text-dim, #999)" }}>
        Redirecting to sign-in…
      </p>
    </div>
  );
}

// ─── Default fallback UI ──────────────────────────────────────

function DefaultFallback({
  error,
  onReset,
}: {
  error: Error;
  onReset: () => void;
}) {
  return (
    <div
      style={{
        padding: "16px",
        margin: "8px",
        borderRadius: "8px",
        background: "var(--bg-card, #1a1a2e)",
        border: "1px solid var(--danger, #e74c3c)",
        color: "var(--text, #eee)",
        fontSize: "14px",
      }}
    >
      <p style={{ margin: "0 0 8px", fontWeight: 600 }}>
        Something went wrong
      </p>
      <p
        style={{
          margin: "0 0 12px",
          fontSize: "12px",
          color: "var(--text-dim, #999)",
          wordBreak: "break-word",
        }}
      >
        {error.message}
      </p>
      <button
        onClick={onReset}
        style={{
          padding: "6px 16px",
          borderRadius: "6px",
          border: "1px solid var(--border-soft, #333)",
          background: "var(--bg, #0f0f23)",
          color: "var(--text, #eee)",
          cursor: "pointer",
          fontSize: "13px",
        }}
      >
        Try Again
      </button>
    </div>
  );
}

// ─── App-root fallback (full-screen reload prompt) ────────────

export function AppRootFallback({
  error,
  onReset,
}: {
  error: Error;
  onReset: () => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        padding: "24px",
        background: "var(--bg, #0f0f23)",
        color: "var(--text, #eee)",
        textAlign: "center",
      }}
    >
      <h1 style={{ fontSize: "20px", margin: "0 0 8px" }}>
        Something went wrong
      </h1>
      <p
        style={{
          fontSize: "14px",
          color: "var(--text-dim, #999)",
          margin: "0 0 16px",
          maxWidth: "400px",
          wordBreak: "break-word",
        }}
      >
        {error.message}
      </p>
      <div style={{ display: "flex", gap: "12px" }}>
        <button
          onClick={onReset}
          style={{
            padding: "8px 20px",
            borderRadius: "6px",
            border: "1px solid var(--border-soft, #333)",
            background: "var(--bg-card, #1a1a2e)",
            color: "var(--text, #eee)",
            cursor: "pointer",
            fontSize: "14px",
          }}
        >
          Try Again
        </button>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: "8px 20px",
            borderRadius: "6px",
            border: "none",
            background: "var(--accent, #646cff)",
            color: "#fff",
            cursor: "pointer",
            fontSize: "14px",
          }}
        >
          Reload Page
        </button>
      </div>
    </div>
  );
}
